// frontend/src/utils/equality.js와 동일 유지
/**
 * ECMAScript의 추상 동등 비교(`==`)가 수행하는 타입 변환을 명시적으로 적용합니다.
 *
 * OCC API의 코드나 ID는 화면에 따라 문자열 또는 숫자로 전달될 수 있어,
 * 기존의 느슨한 비교를 단순히 엄격 비교로 바꾸면 선택·필터 동작이 달라질 수
 * 있습니다. 이 함수는 타입 변환 규칙을 코드로 드러내면서 기존 의미를 유지합니다.
 */
export function isLooseEqual(left, right) {
  const leftType = getEqualityType(left);
  const rightType = getEqualityType(right);

  if (leftType === rightType) return left === right;

  if (
    (leftType === 'Null' && rightType === 'Undefined') ||
    (leftType === 'Undefined' && rightType === 'Null')
  ) {
    return true;
  }

  if (leftType === 'Number' && rightType === 'String') {
    return isLooseEqual(left, Number(right));
  }
  if (leftType === 'String' && rightType === 'Number') {
    return isLooseEqual(Number(left), right);
  }

  if (leftType === 'BigInt' && rightType === 'String') {
    return compareBigIntAndString(left, right);
  }
  if (leftType === 'String' && rightType === 'BigInt') {
    return compareBigIntAndString(right, left);
  }

  if (leftType === 'Boolean') return isLooseEqual(Number(left), right);
  if (rightType === 'Boolean') return isLooseEqual(left, Number(right));

  if (isPrimitiveType(leftType) && rightType === 'Object') {
    return isLooseEqual(left, toPrimitive(right));
  }
  if (leftType === 'Object' && isPrimitiveType(rightType)) {
    return isLooseEqual(toPrimitive(left), right);
  }

  if (leftType === 'BigInt' && rightType === 'Number') {
    return compareBigIntAndNumber(left, right);
  }
  if (leftType === 'Number' && rightType === 'BigInt') {
    return compareBigIntAndNumber(right, left);
  }

  return false;
}

/** null과 함수를 포함한 값을 ECMAScript 명세의 비교 타입으로 구분합니다. */
function getEqualityType(value) {
  if (value === null) return 'Null';
  if (value === undefined) return 'Undefined';

  switch (typeof value) {
    case 'boolean':
      return 'Boolean';
    case 'number':
      return 'Number';
    case 'bigint':
      return 'BigInt';
    case 'string':
      return 'String';
    case 'symbol':
      return 'Symbol';
    default:
      return 'Object';
  }
}

/** 객체와 비교할 수 있는 원시값 타입인지 확인합니다. */
function isPrimitiveType(type) {
  return ['String', 'Number', 'BigInt', 'Symbol'].includes(type);
}

/** BigInt와 문자열 비교에서 잘못된 숫자 문자열은 예외 대신 false로 처리합니다. */
function compareBigIntAndString(bigIntValue, stringValue) {
  try {
    return bigIntValue === window.BigInt(stringValue);
  } catch (error) {
    return false;
  }
}

/** BigInt와 Number는 유한한 정수일 때만 정밀한 정수 값으로 비교합니다. */
function compareBigIntAndNumber(bigIntValue, numberValue) {
  if (!Number.isFinite(numberValue) || !Number.isInteger(numberValue)) return false;
  return bigIntValue === window.BigInt(numberValue);
}

/** Symbol.toPrimitive, valueOf, toString 순서로 객체를 원시값으로 변환합니다. */
function toPrimitive(value) {
  const exoticConverter = value[Symbol.toPrimitive];
  if (exoticConverter !== undefined) {
    const converted = exoticConverter.call(value, 'default');
    if (getEqualityType(converted) !== 'Object') return converted;
    throw new TypeError('Symbol.toPrimitive must return a primitive value');
  }

  for (const methodName of ['valueOf', 'toString']) {
    const converter = value[methodName];
    if (typeof converter !== 'function') continue;
    const converted = converter.call(value);
    if (getEqualityType(converted) !== 'Object') return converted;
  }

  throw new TypeError('Cannot convert object to primitive value');
}
